import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useTier } from '@/context/TierContext';
import { useLanguage } from '@/context/LanguageContext';
import { Check, Shield, Zap, Star, ArrowLeft, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import MobileNav from '@/components/MobileNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All text translated in 3 languages
const TX = {
  en: {
    title: 'Choose your plan',
    subtitle: 'Secure payment — Visa, Mastercard, Apple Pay, Google Pay',
    currentPlan: 'Current plan',
    selected: 'Selected',
    payBtn: (price, name) => `Pay €${price}/month — ${name}`,
    loading: 'Initializing...',
    secureSSL: 'SSL Secure payment',
    cancelAnytime: 'Cancel anytime',
    legalNotice: (tier) => `By subscribing, you agree to our Terms. Do Not Bull is a brand of Strategy Intelligence Solutions LTD (No. 16760736).`,
    successTitle: 'Payment successful!',
    successDesc: (name) => `Your ${name} subscription is active.`,
    successRedirect: 'Redirecting to dashboard...',
    popularBadge: 'MOST POPULAR',
    features: {
      standard: ['Unlimited financial news', 'Portfolio (3 assets)', 'Academy — Modules 1 & 2', 'Trading Simulator', 'AI Recommendations'],
      premium: ['Unlimited news', 'Unlimited portfolio', 'AI Gems detector', 'Chart Analyzer', 'Full Academy (5 modules)', 'AI Chat 24/7', 'Pro Analysis (Goldman, McKinsey...)', 'Real-time Market Sentiment'],
      pro: ['Everything in Premium +', 'Institutional AI Analysis', 'Goldman Sachs, BlackRock, McKinsey', 'Priority AI access', 'Priority support 24/7', 'Early access to new features'],
    },
  },
  fr: {
    title: 'Choisissez votre plan',
    subtitle: 'Paiement sécurisé — Visa, Mastercard, Apple Pay, Google Pay',
    currentPlan: 'Plan actuel',
    selected: 'Sélectionné',
    payBtn: (price, name) => `Payer €${price}/mois — ${name}`,
    loading: 'Initialisation...',
    secureSSL: 'Paiement sécurisé SSL',
    cancelAnytime: 'Annulation à tout moment',
    legalNotice: (tier) => `En souscrivant, vous acceptez nos Conditions. Do Not Bull est une marque de Strategy Intelligence Solutions LTD (No. 16760736).`,
    successTitle: 'Paiement réussi !',
    successDesc: (name) => `Votre abonnement ${name} est activé.`,
    successRedirect: 'Redirection vers le dashboard...',
    popularBadge: 'LE PLUS POPULAIRE',
    features: {
      standard: ['Actualités illimitées', 'Portfolio (3 actifs)', 'Académie — Modules 1 & 2', 'Simulateur de Trading', 'Recommandations IA'],
      premium: ['Actualités illimitées', 'Portfolio illimité', 'Pépites IA complètes', 'Analyseur de Graphiques IA', 'Académie complète (5 modules)', 'Chat IA 24/7', 'Analyse Pro (Goldman, McKinsey...)', 'Sentiment Marché IA temps réel'],
      pro: ['Tout Premium +', 'Analyses Institutionnelles IA', 'Goldman Sachs, BlackRock, McKinsey', 'Accès IA prioritaire', 'Support prioritaire 24/7', 'Accès anticipé nouveautés'],
    },
  },
  es: {
    title: 'Elige tu plan',
    subtitle: 'Pago seguro — Visa, Mastercard, Apple Pay, Google Pay',
    currentPlan: 'Plan actual',
    selected: 'Seleccionado',
    payBtn: (price, name) => `Pagar €${price}/mes — ${name}`,
    loading: 'Inicializando...',
    secureSSL: 'Pago seguro SSL',
    cancelAnytime: 'Cancelar en cualquier momento',
    legalNotice: (tier) => `Al suscribirte, aceptas nuestros Términos. Do Not Bull es una marca de Strategy Intelligence Solutions LTD (No. 16760736).`,
    successTitle: '¡Pago exitoso!',
    successDesc: (name) => `Tu suscripción ${name} está activa.`,
    successRedirect: 'Redirigiendo al dashboard...',
    popularBadge: 'MÁS POPULAR',
    features: {
      standard: ['Noticias ilimitadas', 'Cartera (3 activos)', 'Academia — Módulos 1 y 2', 'Simulador de Trading', 'Recomendaciones IA'],
      premium: ['Noticias ilimitadas', 'Cartera ilimitada', 'Gemas IA completas', 'Analizador de Gráficos IA', 'Academia completa (5 módulos)', 'Chat IA 24/7', 'Análisis Pro (Goldman, McKinsey...)', 'Sentimiento Mercado IA tiempo real'],
      pro: ['Todo Premium +', 'Análisis Institucional IA', 'Goldman Sachs, BlackRock, McKinsey', 'Acceso IA prioritario', 'Soporte prioritario 24/7', 'Acceso anticipado a novedades'],
    },
  },
};

const PLANS = [
  { id: 'premium', name: 'Full Access', price: 19, popular: true, color: '#ff6b35', borderColor: 'border-orange-500/50', bgColor: 'bg-orange-500/10', textColor: 'text-orange-400' },
];

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { tier, refreshTier } = useTier();
  const { language } = useLanguage();
  const tx = TX[language] || TX.en;

  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'premium');
  const [loading, setLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const revolutInstanceRef = useRef(null);

  const currentPlan = PLANS.find(p => p.id === selectedPlan);

  const initRevolutCheckout = async () => {
    if (revolutInstanceRef.current) {
      try { revolutInstanceRef.current.destroy(); } catch {}
      revolutInstanceRef.current = null;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payments/revolut/create-order`,
        { plan_id: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { token: orderToken, order_id } = res.data;

      const { default: RevolutCheckout } = await import('@revolut/checkout');
      const instance = await RevolutCheckout(orderToken, 'prod');

      instance.payWithPopup({
        onSuccess: async () => {
          setPaymentSuccess(true);
          await pollOrderStatus(order_id);
        },
        onError: (error) => {
          toast.error(language === 'fr' ? 'Paiement échoué — ' : language === 'es' ? 'Pago fallido — ' : 'Payment failed — ' + (error?.message || ''));
          setLoading(false);
        },
        onCancel: () => {
          toast.info(language === 'fr' ? 'Paiement annulé' : language === 'es' ? 'Pago cancelado' : 'Payment cancelled');
          setLoading(false);
        },
      });
      revolutInstanceRef.current = instance;
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(language === 'fr' ? 'Erreur d\'initialisation du paiement' : language === 'es' ? 'Error al inicializar el pago' : 'Payment initialization error');
    } finally {
      setLoading(false);
    }
  };

  const pollOrderStatus = async (orderId) => {
    let attempts = 0;
    while (attempts < 20) {
      try {
        const res = await axios.get(`${API}/payments/revolut/status/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
        const status = res.data.status?.toLowerCase();
        if (status === 'completed') {
          await refreshTier();
          toast.success(tx.successDesc(currentPlan?.name));
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
        if (status === 'failed' || status === 'cancelled') {
          toast.error(language === 'fr' ? 'Paiement non confirmé' : language === 'es' ? 'Pago no confirmado' : 'Payment not confirmed');
          return;
        }
      } catch {}
      await new Promise(r => setTimeout(r, 2000));
      attempts++;
    }
    await refreshTier();
    navigate('/dashboard');
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-black text-white mb-3">{tx.successTitle}</h1>
          <p className="text-gray-400 mb-2">{tx.successDesc(currentPlan?.name)}</p>
          <p className="text-gray-500 text-sm">{tx.successRedirect}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="checkout" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4 text-sm">
            <ArrowLeft className="w-4 h-4" />
            {language === 'fr' ? 'Retour' : language === 'es' ? 'Volver' : 'Back'}
          </button>
          <h1 className="text-3xl font-black text-white">{tx.title}</h1>
          <p className="text-gray-400 mt-1">{tx.subtitle}</p>
        </div>

        {/* Current tier notice */}
        {tier && tier !== 'free' && (
          <div className="glass rounded-xl p-4 mb-6 flex items-center gap-2">
            <Check className="w-4 h-4 text-green-400" />
            <span className="text-sm text-gray-300">{tx.currentPlan} : <span className="text-green-400 font-semibold capitalize">{tier}</span></span>
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {PLANS.map(plan => {
            const features = tx.features[plan.id] || [];
            return (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative glass rounded-2xl p-6 cursor-pointer transition-all border-2 ${selectedPlan === plan.id ? plan.borderColor : 'border-gray-800 hover:border-gray-600'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#ff6b35] text-black text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                      {tx.popularBadge}
                    </span>
                  </div>
                )}

                <div className={`w-10 h-10 rounded-xl ${plan.bgColor} flex items-center justify-center mb-4`}>
                  {plan.id === 'standard' ? <Star className="w-5 h-5" style={{ color: plan.color }} /> :
                   plan.id === 'premium' ? <Zap className="w-5 h-5" style={{ color: plan.color }} /> :
                   <Shield className="w-5 h-5" style={{ color: plan.color }} />}
                </div>

                <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-black" style={{ color: plan.color }}>€{plan.price}</span>
                  <span className="text-gray-500 text-sm">/{language === 'fr' ? 'mois' : language === 'es' ? 'mes' : 'mo'}</span>
                </div>

                <ul className="space-y-2">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: plan.color }} />
                      <span className="text-gray-300">{f}</span>
                    </li>
                  ))}
                </ul>

                {selectedPlan === plan.id && (
                  <div className={`mt-4 text-xs font-bold ${plan.textColor} flex items-center gap-1`}>
                    <Check className="w-3 h-3" /> {tx.selected}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Payment Button */}
        <div className="max-w-md mx-auto">
          <Button
            data-testid="pay-now-btn"
            onClick={initRevolutCheckout}
            disabled={loading}
            className="w-full py-6 text-lg font-black rounded-2xl"
            style={{ backgroundColor: currentPlan?.color, color: '#000' }}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                {tx.loading}
              </div>
            ) : (
              <div className="flex items-center gap-2 justify-center">
                <Lock className="w-4 h-4" />
                {tx.payBtn(currentPlan?.price, currentPlan?.name)}
              </div>
            )}
          </Button>

          {/* Security badges */}
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Shield className="w-3.5 h-3.5" />
              {tx.secureSSL}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Lock className="w-3.5 h-3.5" />
              {tx.cancelAnytime}
            </div>
          </div>

          <p className="text-center text-xs text-gray-600 mt-3">
            {tx.legalNotice(selectedPlan)}
            {' '}
            <button onClick={() => navigate('/terms')} className="text-orange-500 hover:underline">
              {language === 'fr' ? 'Conditions' : language === 'es' ? 'Términos' : 'Terms'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
